import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Box, Dialog, DialogTitle, DialogContent, IconButton, InputAdornment, TextField, Typography, Popover, Popper, Paper, ClickAwayListener, List, ListItemButton, ListItemText, Tooltip } from '@mui/material';
import { Search, ExpandMore, Close, ArrowForward, Logout, PersonOutline, Apps, OpenInNew } from '@mui/icons-material';
import { useAuth } from '../../../core/context/AuthContext';
import ThemeToggle from '../ThemeToggle';
import NotificationBell from '../NotificationBell';
import { getNavigation, getDestinations, searchDestinations } from './navigation';
import './Header.css';

const Header = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  const [areaId, setAreaId] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [accountAnchor, setAccountAnchor] = useState(null);
  const searchInput = useRef(null);
  const closeTimer = useRef(null);
  const panelRef = useRef(null);
  const keyboardOpen = useRef(false);
  const areas = useMemo(() => getNavigation(user?.menus), [user?.menus]);
  const entries = useMemo(() => getDestinations(areas), [areas]);
  const activeArea = areas.find(area => area.groups.some(group => group.items.some(item => item.path === location.pathname)));
  const current = entries.find(item => item.path === location.pathname);
  const openedArea = areas.find(area => area.id === areaId);
  const results = searchDestinations(entries, query);
  const isTest = /prueba|test|localhost/.test(window.location.hostname);

  const keepAreaOpen = () => clearTimeout(closeTimer.current);
  const closeArea = () => { keepAreaOpen(); setAnchor(null); };
  const leaveArea = () => {
    keepAreaOpen();
    closeTimer.current = setTimeout(() => {
      if (!panelRef.current?.contains(document.activeElement)) setAnchor(null);
    }, 220);
  };
  const openArea = (event, id, keyboard = false) => {
    keepAreaOpen(); keyboardOpen.current = keyboard;
    setAnchor(event.currentTarget); setAreaId(id);
  };
  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useEffect(() => {
    if (!anchor) return;
    if (keyboardOpen.current) panelRef.current?.querySelector('a')?.focus();
    const onEscape = event => {
      if (event.key === 'Escape') { clearTimeout(closeTimer.current); setAnchor(null); anchor.focus(); }
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [anchor, areaId]);

  useEffect(() => { setAnchor(null); setSearchOpen(false); setAccountAnchor(null); setQuery(''); }, [location.pathname]);
  useEffect(() => {
    const onKey = event => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault(); setAnchor(null); setSearchOpen(value => !value);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const openSearch = () => { setAnchor(null); setQuery(''); setSearchOpen(true); };
  const visit = item => {
    setAnchor(null); setSearchOpen(false); setQuery('');
    if (item.path !== 'legacy') { navigate(item.path); return; }
    // Preserve the existing legacy sign-in handoff only when that destination is chosen.
    let auth = '';
    try {
      const stored = JSON.parse(localStorage.getItem('credentials') || 'null');
      if (stored) auth = `?u=${encodeURIComponent(stored.user)}&p=${encodeURIComponent(stored.password)}`;
    } catch {}
    window.location.assign(`${process.env.REACT_APP_DOMAIN || 'https://test.sugi.com.ar'}/GrillaFacturacionesTecnicos.aspx${auth}`);
  };
  const linkProps = item => ({
    component: item.path === 'legacy' ? 'button' : Link,
    to: item.path === 'legacy' ? undefined : item.path,
    onClick: event => {
      if (!event.ctrlKey && !event.metaKey && !event.shiftKey) { event.preventDefault(); visit(item); }
    },
  });

  return (
    <header className="sugi-navigation">
      <div className="sugi-navigation-top">
        <Link to="/" className="sugi-navigation-brand">SUGI{isTest && <span>PRUEBAS</span>}</Link>
        <Button className="sugi-navigation-search" startIcon={<Search />} onClick={openSearch} aria-label="Buscar pantalla">
          <span>Buscar pantalla…</span><kbd>Ctrl K</kbd>
        </Button>
        <div className="sugi-navigation-utilities">
          <div className="sugi-navigation-theme"><ThemeToggle /></div>
          <NotificationBell />
          <Tooltip title="Mi cuenta"><Button className="sugi-navigation-account" startIcon={<PersonOutline />} onClick={event => setAccountAnchor(event.currentTarget)} aria-haspopup="dialog" aria-expanded={Boolean(accountAnchor)}>{user?.username}</Button></Tooltip>
        </div>
      </div>
      <div className="sugi-navigation-bottom">
        <nav aria-label="Navegación principal" className="sugi-navigation-areas">
          {areas.map(area => <Button key={area.id} className={`sugi-navigation-area ${activeArea?.id === area.id ? 'is-current' : ''}`} endIcon={<ExpandMore />} aria-expanded={Boolean(anchor) && areaId === area.id} aria-controls={anchor && areaId === area.id ? 'sugi-area-panel' : undefined} aria-haspopup="dialog"
            onMouseEnter={event => openArea(event, area.id)} onMouseLeave={leaveArea}
            onClick={event => openArea(event, area.id, event.detail === 0)}
            onKeyDown={event => { if (event.key === 'ArrowDown') { event.preventDefault(); openArea(event, area.id, true); } }}>{area.label}</Button>)}
        </nav>
        {current && <span className="sugi-navigation-location">Estás en <strong>{current.label}</strong></span>}
        <Button className="sugi-navigation-mobile" startIcon={<Apps />} onClick={openSearch}>Pantallas</Button>
      </div>
      <Popper open={Boolean(anchor) && Boolean(openedArea)} anchorEl={anchor} placement="bottom-start" modifiers={[{ name: 'offset', options: { offset: [0, 8] } }, { name: 'preventOverflow', options: { padding: 16 } }]} sx={{ zIndex: 12000 }}>
        <ClickAwayListener onClickAway={event => { if (!anchor?.contains(event.target)) closeArea(); }}>
        <Paper ref={node => { panelRef.current = node; if (node && keyboardOpen.current) node.querySelector('a')?.focus(); }} id="sugi-area-panel" role="dialog" aria-label={openedArea?.label} onMouseEnter={keepAreaOpen} onMouseLeave={leaveArea}
          onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget) && !anchor?.contains(event.relatedTarget)) closeArea(); }}
          elevation={8} sx={{ p: { xs: 2, sm: 3 }, width: openedArea?.groups.length > 2 ? 1050 : 650, maxWidth: 'calc(100vw - 32px)', maxHeight: 'calc(100dvh - 180px)', overflowY: 'auto', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <div><Typography variant="h6">{openedArea?.label}</Typography><Typography variant="body2" color="text.secondary">Elegí la tarea que necesitás realizar.</Typography></div>
          <IconButton aria-label="Cerrar navegación" onClick={closeArea}><Close /></IconButton>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: `repeat(${Math.min(openedArea?.groups.length || 1, 2)}, minmax(0, 1fr))`, lg: `repeat(${Math.min(openedArea?.groups.length || 1, 4)}, minmax(0, 1fr))` }, gap: 2 }}>
          {openedArea?.groups.map(group => <section key={group.label} aria-label={group.label}>
            <Typography component="h3" sx={{ fontSize: '.75rem', fontWeight: 700, color: 'text.secondary', px: 2, mb: 1 }}>{group.label}</Typography>
            <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: .75 }}>{group.items.map(item => <ListItemButton key={`${item.label}-${item.path}`} {...linkProps(item)} selected={item.path === location.pathname} aria-current={item.path === location.pathname ? 'page' : undefined}
              sx={{
                borderRadius: 2, minHeight: 46, fontSize: '.875rem', gap: 1, px: 1.5,
                border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover',
                transition: 'background-color 140ms ease, border-color 140ms ease, box-shadow 140ms ease',
                '& > span': { flex: 1 },
                '& .navigation-option-arrow': { color: 'text.secondary', transition: 'transform 140ms ease' },
                '&:hover, &.Mui-focusVisible': { bgcolor: 'primary.softHover', borderColor: 'primary.main', color: 'primary.main', boxShadow: '0 2px 8px rgba(15,23,42,.08)' },
                '&:hover .navigation-option-arrow, &.Mui-focusVisible .navigation-option-arrow': { color: 'primary.main', transform: 'translateX(2px)' },
                '&.Mui-focusVisible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
                '&.Mui-selected': { bgcolor: 'primary.soft', borderColor: 'primary.main', color: 'primary.main', fontWeight: 600 },
              }}><span>{item.label}</span>{item.path === 'legacy' ? <OpenInNew className="navigation-option-arrow" sx={{ fontSize: 16 }} /> : <ArrowForward className="navigation-option-arrow" sx={{ fontSize: 16 }} />}</ListItemButton>)}</List>
          </section>)}
        </Box>
        <Button startIcon={<Search />} onClick={openSearch} sx={{ mt: 2 }}>Buscar en todas las pantallas</Button>
        </Paper>
        </ClickAwayListener>
      </Popper>
      <Dialog open={searchOpen} onClose={() => setSearchOpen(false)} fullWidth maxWidth="sm" TransitionProps={{ onEntered: () => searchInput.current?.focus() }} aria-labelledby="sugi-search-title">
        <DialogTitle id="sugi-search-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>Ir a una pantalla<IconButton aria-label="Cerrar buscador" onClick={() => setSearchOpen(false)}><Close /></IconButton></DialogTitle>
        <DialogContent>
          <TextField fullWidth autoFocus inputRef={searchInput} label="¿Qué necesitás hacer?" placeholder="Ej.: turnos, facturas, pacientes…" value={query} onChange={event => setQuery(event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
            onKeyDown={event => { if (event.key === 'Enter' && results.length === 1) { event.preventDefault(); visit(results[0]); } }} sx={{ mt: 1, mb: 2 }} />
          <Typography variant="body2" color="text.secondary" aria-live="polite">{query ? `${results.length} pantallas encontradas` : 'Todas tus pantallas, agrupadas por área'}</Typography>
          <List sx={{ maxHeight: 'min(52dvh, 440px)', overflowY: 'auto', mt: 1 }} aria-label="Pantallas disponibles">
            {results.map(item => <ListItemButton key={`${item.label}-${item.path}`} {...linkProps(item)} selected={item.path === location.pathname} sx={{ borderRadius: 2, minHeight: 64, gap: 2 }}>
              <ListItemText primary={item.label} secondary={`${item.area} / ${item.group}`} primaryTypographyProps={{ fontSize: '.9375rem', fontWeight: 600 }} secondaryTypographyProps={{ fontSize: '.75rem' }} /><ArrowForward sx={{ fontSize: 18, color: 'text.secondary' }} />
            </ListItemButton>)}
          </List>
          {results.length === 0 && <Typography sx={{ py: 3 }} color="text.secondary">Probá con otra palabra, como «médicos» o «facturas».</Typography>}
        </DialogContent>
      </Dialog>
      <Popover open={Boolean(accountAnchor)} anchorEl={accountAnchor} onClose={() => setAccountAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }} PaperProps={{ sx: { p: 2, mt: 1, minWidth: 240, borderRadius: 3 } }}>
        <Typography sx={{ fontWeight: 600, mb: 2 }}>{user?.username}</Typography>
        <div className="sugi-navigation-account-theme"><ThemeToggle /></div>
        <Button color="primary" startIcon={<Logout />} onClick={() => { logout(); navigate('/login'); }} sx={{ mt: 1 }}>Cerrar sesión</Button>
      </Popover>
    </header>
  );
};
export default Header;
